import React, { useState, forwardRef } from "react"
import { useFormContext } from "react-hook-form"
import axios from "axios"

import { Avatar, Flex, FileInput, Group, Loader, Stack } from "@mantine/core"
import { Icon, Radio, Select } from "@/components"

import { downloadCenterPaths } from "app/static/frontend/imports/desktopPaths"
import icons from "app/static/frontend/imports/icons"
import { useStyles } from "@/components/IconImageUploader/IconImageUploader.styles"

const iconOptions = icons.map((icon) => ({ id: icon, value: icon, label: icon.split("fa-")[1], icon }))

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
    value: string
    label: string
}

type Upload = {
    name: string
    size: number
    type: string
    url: string
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(({ value, label, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
        <Group noWrap>
            <i className={`fa ${value}`} />
            {label}
        </Group>
    </div>
))

export const IconImageUploader = () => {
    const label = "Use Image or Icon"
    const tooltip =
        "Select an icon or upload an image that will visually represent this issue in other areas of the application, such as Power Search"

    const formContext = useFormContext()
    const avatarType = formContext.watch("avatarType")
    const icon = formContext.watch("icon")
    const imageUrl = formContext.watch("imageUrl")
    const [isLoading, setIsLoading] = useState(false)
    const { classes } = useStyles()

    const uploadImage = (image) => {
        setIsLoading(true)
        const formData = new FormData()
        formData.append("0", image)

        axios
            .post<Upload[]>(downloadCenterPaths.uploadAttachment, formData, {
                headers: {
                    Accept: "application/json",
                    "Accept-Language": "en-US,en;q=0.8",
                    "Content-Type": `multipart/form-data;`,
                },
                params: {}, // pass an empty object for params so axios interceptor doesn't complain
            })
            .then((response) => {
                const imageUrl = response[0]?.url
                formContext.setValue("imageUrl", imageUrl)
            })
            .catch((responseError) => {
                console.error(responseError)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    return (
        <section>
            <Stack spacing="sm">
                <Radio.Group label={label} name="avatarType" tooltip={tooltip}>
                    <Stack spacing="xs">
                        <Radio name="avatarType" value="image" label="Image" />
                        <Radio name="avatarType" value="icon" label="Icon" />
                    </Stack>
                </Radio.Group>

                <Flex align="center" gap="sm" justify="flex-start">
                    {avatarType === "image" ? (
                        <div className={classes.container}>
                            <Avatar className={classes.avatar} src={imageUrl ? imageUrl : null} radius="md">
                                {isLoading ? <Loader /> : !imageUrl && <i className={`fa fa-image`} />}
                            </Avatar>
                            <FileInput
                                className={classes.fileInput}
                                icon={<Icon icon="file" iconFamily="far" />}
                                accept="image/png,image/jpeg"
                                size="md"
                                // @ts-expect-error
                                placeholder="Upload an image..."
                                onChange={uploadImage}
                            />
                        </div>
                    ) : (
                        <div className={classes.container}>
                            <Avatar className={classes.avatar} radius="md">
                                <i className={`fa ${icon}`} />
                            </Avatar>
                            <Select
                                className={classes.select}
                                itemComponent={SelectItem}
                                data={iconOptions}
                                name="icon"
                                placeholder="Choose icon..."
                                size="md"
                                aria-label="Icon"
                                onChange={(iconValue) => {
                                    formContext.setValue("icon", iconValue)
                                }}
                                value={icon}
                                searchable
                            />
                        </div>
                    )}
                </Flex>
            </Stack>
        </section>
    )
}
